"use client";

import React, { useEffect, useState } from "react";
import { Box, Button, Container, Skeleton, Typography, useMediaQuery } from "@mui/material";
import PromoCruiseCard from "./PromoCruiseCard";
import { FlyCruise } from "@/interfaces/ICruiseDeals";

import ISwiper from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import ArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { ICruise } from "@/interfaces/IAllCruisesList";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import Link from "next/link";

const PromoDealsLayout: React.FC<{
  flyCruise?: ICruise[];
  oceanCruises?: ICruise[];
  cruiseOnly?: ICruise[];
  riverCruise?: ICruise[];
}> = ({ flyCruise, oceanCruises, cruiseOnly, riverCruise }) => {
  const [processedCruises, setProcessedCruises] = useState<{
    flyCruise: ICruise[];
    oceanCruises: ICruise[];
    cruiseOnly: ICruise[];
    riverCruise: ICruise[];
  }>({
    flyCruise: [],
    oceanCruises: [],
    cruiseOnly: [],
    riverCruise: [],
  });
  const [isLoading, setisLoading] = useState(true);

  // Consolidated processing function
  const processCruiseData = (cruises: ICruise[], flyCruiseOnly: boolean, Onlycruise: boolean) => {
    if (!cruises || cruises.length === 0) return [];

    return cruises
      .map((cruise) => {
        const lowestPriceInfo = getLowestPriceInfo(cruise, flyCruiseOnly, Onlycruise);
        const lowestPrice = parseFloat(lowestPriceInfo.price);

        return {
          ...cruise,
          lowestPrice: isNaN(lowestPrice) || lowestPrice === 0 ? Infinity : lowestPrice,
          lowestPriceInfo,
        };
      })
      .filter((cruise) => cruise.lowestPrice !== Infinity)
      .sort((a, b) => a.lowestPrice - b.lowestPrice);
  };

  useEffect(() => {
    setisLoading(true);
    setProcessedCruises({
      flyCruise: flyCruise ? processCruiseData(flyCruise, true, false) : [], // Only fly cruise prices
      oceanCruises: oceanCruises ? processCruiseData(oceanCruises, false, false) : [],
      cruiseOnly: cruiseOnly ? processCruiseData(cruiseOnly, false, true) : [], // Only cruise prices
      riverCruise: riverCruise ? processCruiseData(riverCruise, false, false) : [],
    });
    setisLoading(false);
  }, [flyCruise, oceanCruises, cruiseOnly, riverCruise]);

  // const filteredFlyCruises = processedCruises.flyCruise
  //   ?.filter((x) => x.flyCruisePrice !== "0.00")
  //   .filter(
  //     (item, index, self) =>
  //       index === self.findIndex((x) => x?.shipImageHref === item?.shipImageHref),
  //   );

  const filteredCruisesOnly = processedCruises.cruiseOnly
    ?.filter((cruise, index, array) => {
      const firstIndex = array.findIndex((item) => item.shipImageHref === cruise.shipImageHref);
      return index === firstIndex;
    })
    .filter((x) => x.cruiseOnlyPrice !== "0.00");

  const filteredOcianCruises = processedCruises.oceanCruises
    .slice(4)
    .filter(
      (item, index, self) =>
        index === self.findIndex((x) => x?.shipImageHref === item?.shipImageHref),
    );

  const filteredRiverCruise = processedCruises.riverCruise.filter(
    (item, index, self) =>
      index === self.findIndex((x) => x?.operatorImageHref === item?.operatorImageHref),
  );

  return (
    <Box>
      <Container>
        <Box sx={{ mb: { xs: "2rem", md: "3rem" } }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "14px", md: "15px" },
              color: "#555",
              mb: "10px",
            }}
          >
            Now is the moment to secure your spot on the most affordable cruise holidays of{" "}
            {new Date().getFullYear()}! We have compiled incredible deals from Oceania Cruises&apos;
            opulent offerings to all inclusive fly cruises.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "14px", md: "15px" },
              color: "#555",
              mb: "10px",
            }}
          >
            Look at our picturesque River Cruises for a one-of-a-kind adventure or our cruise-only
            deals for a great discount. You can discover the best holiday cruise deals at an
            unbeatable price among the numerous options. These sales, however, will not last
            forever, so don&apos;t delay!
          </Typography>
        </Box>

        {/* <CommonPromotion
          title1="Exclusive"
          title2="Fly-Cruise"
          title3="Deals"
          subtitle="Your holiday cruise, flights included! Explore our exclusive fly cruises and cruise holiday deals."
          data={filteredFlyCruises as FlyCruise[]}
          isFlyCruiseHref
          isThreeWordTitle
          isLoading={isLoading}
          promoIndex={1}
        /> */}

        <CommonPromotion
          title1="Exclusive"
          title2="Cruise-Only"
          title3="Deals"
          subtitle="Just the cruise, amazing price! Explore exclusive cruise only deals and cruise holiday deals."
          data={filteredCruisesOnly as FlyCruise[]}
          isThreeWordTitle
          isCruiseOnlyHref
          isLoading={isLoading}
          promoIndex={2}
        />

        <CommonPromotion
          title1="Ocean"
          title2="Cruise Deals"
          subtitle="Experience the finest at sea for less! Explore our Ocean cruise deals and cruise holiday deals."
          data={filteredOcianCruises as FlyCruise[]}
          isOceaniaCruise
          isLoading={isLoading}
          promoIndex={3}
        />

        <CommonPromotion
          title1="Exclusive"
          title2="River Cruise"
          title3="Deals"
          subtitle="Escape the ocean crowds! Find exclusive river cruise options within our cruise holiday deals."
          data={filteredRiverCruise as FlyCruise[]}
          isThreeWordTitle
          isRiverCruiseHref
          isLoading={isLoading}
          promoIndex={4}
        />
      </Container>
    </Box>
  );
};

export default PromoDealsLayout;

export const PromoTitle: React.FC<{
  title: string;
  description: string;
  isOceaniaCruise?: boolean;
  isRiverCruiseHref?: boolean;
  href?: string;
}> = ({ title, description, isOceaniaCruise, isRiverCruiseHref, href }) => {
  return (
    <Box>
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: "1.6rem", md: "2.4rem", lg: "3rem" },
          textAlign: { xs: "center", sm: "left" },
          fontWeight: 700,
          color: "#060273",
          strong: {
            fontWeight: 700,
            color: "#5900ff",
          },
        }}
        dangerouslySetInnerHTML={{
          __html: title,
        }}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
        <Typography
          variant="body2"
          sx={{
            color: "#555",
            fontSize: { xs: "14px", md: "15px" },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          {description}
        </Typography>
        {(isOceaniaCruise || isRiverCruiseHref) && (
          <Link href={href ?? ""}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                color: "#0c4875",
                position: "relative",
                top: "-2px",
                fontWeight: 500,
              }}
            >
              <Typography sx={{ fontSize: "15px" }}>Explore</Typography>
              <OpenInNewRoundedIcon sx={{ fontSize: "17px" }} />
            </Box>
          </Link>
        )}
      </Box>
    </Box>
  );
};

const CommonPromotion: React.FC<{
  data?: FlyCruise[];
  title1: string;
  title2: string;
  title3?: string;
  subtitle: string;
  isThreeWordTitle?: boolean;
  isOceaniaCruise?: boolean;
  isFlyCruiseHref?: boolean;
  isCruiseOnlyHref?: boolean;
  isRiverCruiseHref?: boolean;
  isLoading: boolean;
  promoIndex: number;
}> = ({
  data,
  title1,
  title2,
  title3,
  subtitle,
  isThreeWordTitle,
  isOceaniaCruise,
  isRiverCruiseHref,
  isLoading,
  promoIndex,
}) => {
  const [swiperRef, setSwiperRef] = useState<ISwiper>();
  const [activeSlide, setActiveSlide] = useState(0);

  function formatUrlSpace(input: string) {
    let result = input.replace(/[\s+&]/g, "+");
    result = result.replace(/-{2,}/g, "+");
    return result;
  }

  const desktopSize = useMediaQuery("(min-width: 1200px)");
  const tabSize = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  const lapSize = useMediaQuery("(min-width: 900px) and (max-width: 1200px)");

  const slideLength = desktopSize
    ? (data?.length ?? 0) - 4
    : lapSize
    ? (data?.length ?? 0) - 3
    : tabSize
    ? (data?.length ?? 0) - 2
    : (data?.length ?? 0) - 1;
  return (
    <>
      <Box
        sx={{
          display: { sm: "flex" },
          justifyContent: { sm: "space-between" },
          alignItems: { sm: "center" },
          width: "100%",
        }}
      >
        <PromoTitle
          title={`${title1} <strong>${title2}</strong> ${isThreeWordTitle ? title3 : ""}`}
          description={subtitle}
          isOceaniaCruise={isOceaniaCruise}
          isRiverCruiseHref={isRiverCruiseHref}
          href={isOceaniaCruise ? "/ocean-cruises" : isRiverCruiseHref ? "/river-cruises" : ""}
        />

        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            gap: "10px",
          }}
        >
          <Button
            disableElevation
            disabled={activeSlide == 0}
            sx={{
              p: "8px",
              borderRadius: "630px",
              minWidth: "unset",
              zIndex: 2,
              bgcolor: "#e30053",
              color: "#fff",
              border: "1px solid",
              borderColor: "#fff",
              boxShadow: "0px 0px 14px rgba(0,0,0,0.5)",
              "&:hover": {
                bgcolor: "#fff",
                color: "#e30053",
                borderColor: "#e30053",
              },

              svg: {
                fontSize: "2rem",
              },
            }}
            onClick={() => swiperRef?.slidePrev()}
            aria-label="Previous slide"
          >
            <ArrowLeftIcon />
          </Button>
          <Button
            disableElevation
            disabled={activeSlide == slideLength}
            sx={{
              p: "8px",
              borderRadius: "30px",
              minWidth: "unset",
              zIndex: 1,
              bgcolor: "#e30053",
              color: "#fff",
              border: "1px solid",
              borderColor: "#fff",
              boxShadow: "0px 0px 14px rgba(0,0,0,0.5)",
              "&:hover": {
                bgcolor: "#fff",
                color: "#e30053",
                borderColor: "#e30053",
              },
              svg: {
                fontSize: "2rem",
              },
            }}
            onClick={() => swiperRef?.slideNext()}
            aria-label="Next slide"
          >
            <ArrowRightIcon />
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          mt: "1rem",
          mb: "2rem",
        }}
      >
        {isLoading ? (
          <Box
            key={promoIndex}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                small: "1fr 1fr",
                md: "1fr 1fr 1fr 1fr",
              },
              gap: "1rem",
            }}
          >
            {[...new Array(6)].map(() => (
              <Box
                key={`${promoIndex}-loading`}
                sx={{
                  height: "218px",
                  position: "relative",
                }}
              >
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "15px",
                  }}
                />
              </Box>
            ))}
          </Box>
        ) : data && data?.length > 0 ? (
          <Box
            sx={{
              position: "relative",
              ".swiper-pagination-bullet": {
                width: "10px",
                height: "10px",
              },
              ".destination_card_img .swiper-pagination-bullet": {
                width: { xs: "20px !important", md: "18px !important" },
                height: {
                  xs: "20px !important",
                  md: "18px !important",
                },
                background: "#0C4875",
                fontSize: "11px",
                color: "#fff",
                display: "inline-grid",
                placeItems: "center",
                borderRadius: "10px",
                span: {
                  mb: { xs: "-1px", md: "0px" },
                  ml: { xs: "0px", md: "0px" },
                },
              },
              mt: "32px",
            }}
          >
            <Swiper
              onSwiper={(s) => {
                setSwiperRef(s);
              }}
              onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
              pagination={{
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 8,
                renderBullet: function (index, className) {
                  return '<span class="' + className + '"><span>' + (index + 1) + "</span></span>";
                },
              }}
              slidesPerView={1.1}
              slidesPerGroup={1}
              spaceBetween={16}
              breakpoints={{
                600: {
                  slidesPerView: 2,
                  spaceBetween: 16,
                },
                900: {
                  slidesPerView: 3,
                  spaceBetween: 20,
                },
                1200: {
                  slidesPerView: 4,
                  spaceBetween: 20,
                },
              }}
              modules={[Navigation, Pagination]}
              className="destination_card_img"
              style={{
                height: "100%",
                margin: "-1rem -1rem 0rem -1rem",
                padding: "1rem 1rem 3rem 1rem",
              }}
            >
              {data?.map((cruise, xI) => (
                <SwiperSlide key={xI}>
                  <PromoCruiseCard
                    cruise={cruise}
                    href={
                      isOceaniaCruise
                        ? `/ocean-cruises?operator=${
                            cruise?.operator ?? ""
                          }&shipName=${formatUrlSpace(cruise?.shipTitle ?? "")}`
                        : isRiverCruiseHref
                        ? `/river-cruises?operator=${
                            cruise?.operator ?? ""
                          }&shipName=${formatUrlSpace(cruise?.shipTitle ?? "")}`
                        : `/cruise-results?operator=${
                            cruise?.operator ?? ""
                          }&shipName=${formatUrlSpace(cruise?.shipTitle ?? "")}`
                    }
                    sx={{
                      boxShadow: "0px 0px 15px #C1BEFFaa",
                      border: "2px solid #C1BEFF",
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        ) : (
          <Box>Not found</Box>
        )}
      </Box>
    </>
  );
};

const extractPricesFromCruise = (cruise: ICruise, flyCruiseOnly: boolean, cruiseOnly: boolean) => {
  const prices = [];

  if (
    cruiseOnly === true &&
    (cruise.cruiseOnlyPrice ||
      cruise.insidePrice ||
      cruise.outsidePrice ||
      cruise.balconyPrice ||
      cruise.outsidePrice)
  ) {
    prices.push({
      price: Number(cruise?.cruiseOnlyPrice),
      type: "Cruise Only",
      displayName: "(Cruise Only)",
    });
  } else if (
    flyCruiseOnly === true &&
    (cruise?.flyCruisePrice ||
      cruise.insidePrice ||
      cruise.outsidePrice ||
      cruise.balconyPrice ||
      cruise.outsidePrice)
  ) {
    prices.push({
      price: Number(cruise.flyCruisePrice),
      type: "Fly Cruise",
      displayName: "(with Flight)",
    });
  } else {
    // Add all available prices when no specific filter is applied
    if (cruise.cruiseOnlyPrice) {
      prices.push({
        price: Number(cruise.cruiseOnlyPrice),
        type: "Cruise Only",
        displayName: "",
      });
    }
    if (cruise.flyCruisePrice) {
      prices.push({
        price: Number(cruise.flyCruisePrice),
        type: "Fly Cruise",
        displayName: "(with Flight)",
      });
    }
    if (cruise.insidePrice) {
      prices.push({
        price: Number(cruise.insidePrice),
        type: "Inside Cabin",
        displayName: "(Inside Cabin)",
      });
    }
    if (cruise.outsidePrice) {
      prices.push({
        price: Number(cruise.outsidePrice),
        type: "Outside Cabin",
        displayName: "(Outside Cabin)",
      });
    }
    if (cruise.balconyPrice) {
      prices.push({
        price: Number(cruise.balconyPrice),
        type: "Balcony",
        displayName: "(Balcony)",
      });
    }
    if (cruise.suitePrice) {
      prices.push({
        price: Number(cruise.suitePrice),
        type: "Suite",
        displayName: "(Suite)",
      });
    }
  }

  return prices.filter((p) => p.price > 0 && !isNaN(p.price));
};

const getLowestPriceInfo = (cruise: ICruise, flyCruiseOnly: boolean, cruiseOnly: boolean) => {
  const prices = extractPricesFromCruise(cruise, flyCruiseOnly, cruiseOnly);

  if (prices.length === 0) {
    return { price: "0.00", type: "", displayName: "" };
  }

  const sortedPrices = [...prices].sort((a, b) => a.price - b.price);
  const lowest = sortedPrices[0];

  return {
    price: lowest.price.toFixed(2),
    type: lowest.type,
    displayName: lowest.displayName,
  };
};
